/* eslint-disable react/jsx-curly-newline */
import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertBanner,
  Box,
  Button,
  FooterActionBar,
  InputField,
  MenuInput,
  Typography,
} from '@saviynt/design-system';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

import { type RootState } from '../../../../../services/types';
import { isEmpty, useInvertedLocalize } from '../../../../../utils';
import { type Option } from '../../../data-layer/constants';
import { type IDPConfigKosmos, type Provider } from '../../../data-layer/types';
import { usePostIDProvider, useUpdateIDPProvider } from '../../../hooks';
import { Header } from '../../common';

import invertedMessages from './messages';

import './SaviyntIDV.css';

interface IDPSetupPageProps {
  isOpen: boolean;
  onClose: () => void;
  provider: Provider | undefined;
  currentProvider: IDPConfigKosmos | undefined;
  refetchIDPProviders: () => void;
  setShowSuccessBanner: (val: boolean) => void;
  currentVendor: Provider | undefined;
}

interface VendorConfig {
  DVCID?: string;
  DOCUMENT_TYPE?: string;
  LICENSE?: string;
  TENANT_DNS?: string;
  TENANT_URL?: string;
  COMMUNITY_NAME?: string;
}

function SaviyntIDVSettings({
  isOpen,
  onClose,
  provider,
  currentProvider,
  refetchIDPProviders,
  setShowSuccessBanner,
  currentVendor,
}: IDPSetupPageProps) {
  const [documentType, setDocumentType] = useState(
    currentProvider?.config?.DOCUMENT_TYPE
  );
  const [tenantDNS, setTenantDNS] = useState(
    currentProvider?.config?.TENANT_DNS
  );
  const [tenantURL, setTenantURL] = useState(
    currentProvider?.config?.TENANT_URL ?? undefined
  );
  const [dvcID, setDvcID] = useState(currentProvider?.config?.DVCID);
  const [license, setLicense] = useState(
    currentProvider?.config?.LICENSE ?? ''
  );
  const [isLicenseVisible, setIsLicenseVisible] = useState(false);
  const [isErrorAlertVisible, setIsErrorAlertVisible] = useState(false);
  const [showCriticalBanner, setShowCriticalBanner] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [community, setCommunity] = useState(
    currentProvider?.config?.COMMUNITY_NAME
  );
  const { isMenuPinned, userInfo } = useSelector(
    (state: RootState) => state.user
  );
  const isSaviyntSupportAdmin = (userInfo?.roles ?? []).includes(
    'ROLE_ADMIN_SAVIYNTSUPPORT'
  );

  const l = useInvertedLocalize(invertedMessages);

  useEffect(() => {
    if (currentProvider) {
      setLicense(currentProvider.config?.LICENSE ?? '');
      setTenantDNS(currentProvider.config?.TENANT_DNS);
      setTenantURL(currentProvider.config?.TENANT_URL);
      setDvcID(currentProvider.config?.DVCID);
      setDocumentType(currentProvider.config?.DOCUMENT_TYPE);
      setCommunity(currentProvider.config?.COMMUNITY_NAME);
    }
  }, [currentProvider]);

  const { mutate: saveIDPConfig } = usePostIDProvider();
  const { mutate: updateIDPConfig, isPending: isUpdateInProgress } =
    useUpdateIDPProvider();

  const handleSave = () => {
    if (provider && !isEmpty(currentProvider)) {
      const vendorConfig: {
        vendorName: string;
        status: number;
        config: VendorConfig;
      } = {
        vendorName: provider.id,
        status: 0,
        config: isSaviyntSupportAdmin
          ? {
              DOCUMENT_TYPE: documentType,
              DVCID: dvcID,
              TENANT_DNS: tenantDNS,
              TENANT_URL: tenantURL,
              COMMUNITY_NAME: community,
            }
          : { DOCUMENT_TYPE: documentType, DVCID: dvcID },
      };

      if (!isEmpty(currentProvider)) {
        vendorConfig.status = currentProvider?.status ?? 0;
      }

      // Only include LICENSE when it has changed and the user has permission
      if (
        isSaviyntSupportAdmin &&
        !isEmpty(currentProvider) &&
        license !== currentProvider?.config?.LICENSE &&
        license
      ) {
        vendorConfig.config.LICENSE = license;
      }

      // Prevent saving with an empty license if it was cleared
      if (isSaviyntSupportAdmin && license.trim() === '') {
        setIsErrorAlertVisible(true);

        return;
      }

      setIsErrorAlertVisible(false);

      updateIDPConfig(vendorConfig as IDPConfigKosmos, {
        onSuccess(response) {
          if (response.errorMessage) {
            setErrorMessage(response.errorMessage);
            setShowCriticalBanner(true);
          } else {
            onClose();
            refetchIDPProviders();
            setShowSuccessBanner(true);
          }
        },
      });
    } else if (currentVendor) {
      const vendorConfig = {
        vendorName: currentVendor.id,
        status: 1,
        config: {
          DOCUMENT_TYPE: documentType,
          DVCID: dvcID,
          ...(isSaviyntSupportAdmin
            ? {
                TENANT_DNS: tenantDNS,
                TENANT_URL: tenantURL,
                COMMUNITY_NAME: community,
                ...(license ? { LICENSE: license } : {}),
              }
            : {}),
        },
      };

      saveIDPConfig(vendorConfig as IDPConfigKosmos, {
        onSuccess(response) {
          if (response.errorMessage) {
            setErrorMessage(response.errorMessage);
            setShowCriticalBanner(true);
          } else {
            onClose();
            refetchIDPProviders();
          }
        },
      });
    }
  };

  const licenseField = useMemo(
    () => (
      <InputField
        name='license'
        type={isLicenseVisible ? 'text' : 'password'}
        suffixButtonType='password'
        suffixIsToggle={isLicenseVisible}
        suffixOnClick={() => setIsLicenseVisible((prev) => !prev)}
        label={
          <Typography kind='label' htmlFor='License'>
            {l('License key')}
          </Typography>
        }
        placeholder={l('Enter License key')}
        value={license}
        setValue={setLicense}
        isCritical={isErrorAlertVisible}
        setIsCritical={setIsErrorAlertVisible}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setLicense(e.target.value)
        }
        className='Ext-SaviyntIDVSettings-content-inputField'
        isRequired
        isDisabled={!isSaviyntSupportAdmin}
        maxLength={100}
        minRequiredChars={3}
      />
    ),
    [
      license,
      setLicense,
      l,
      isLicenseVisible,
      isErrorAlertVisible,
      setIsErrorAlertVisible,
      isSaviyntSupportAdmin,
    ]
  );

  const dvcIDField = useMemo(
    () => (
      <InputField
        name='dvcID'
        label={
          <Typography kind='label' htmlFor='dvcID'>
            {l('Verification Config ID')}
          </Typography>
        }
        placeholder={l('Enter Verification Config ID')}
        value={dvcID}
        setValue={setDvcID}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setDvcID(e.target.value)
        }
        className='Ext-SaviyntIDVSettings-content-inputField'
        isRequired
        maxLength={100}
        minRequiredChars={3}
      />
    ),
    [dvcID, setDvcID, l]
  );

  const communityField = useMemo(
    () => (
      <InputField
        name='community'
        label={
          <Typography kind='label' htmlFor='Community Name'>
            {l('Community Name')}
          </Typography>
        }
        placeholder={l('Enter Community Name')}
        value={community}
        setValue={setCommunity}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setCommunity(e.target.value)
        }
        className='Ext-SaviyntIDVSettings-content-inputField'
        maxLength={255}
        isRequired
        minRequiredChars={3}
      />
    ),
    [community, setCommunity, l]
  );

  const tenantDNSField = useMemo(
    () => (
      <InputField
        name='tenantDNS'
        label={
          <Typography kind='label' htmlFor='tenantDNS'>
            {l('Tenant DNS')}
          </Typography>
        }
        placeholder={l('Enter Tenant DNS')}
        value={tenantDNS}
        setValue={setTenantDNS}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setTenantDNS(e.target.value)
        }
        className='Ext-SaviyntIDVSettings-content-inputField'
        isRequired
        maxLength={255}
        minRequiredChars={3}
      />
    ),
    [tenantDNS, setTenantDNS, l]
  );

  const tenantURLField = useMemo(
    () => (
      <InputField
        name='tenantURL'
        label={
          <Typography kind='label' htmlFor='tenantURL'>
            {l('Tenant URL')}
          </Typography>
        }
        placeholder={l('Enter Tenant URL')}
        value={tenantURL}
        setValue={setTenantURL}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setTenantURL(e.target.value);
        }}
        className='Ext-SaviyntIDVSettings-content-inputField'
        isRequired
        maxLength={255}
        minRequiredChars={3}
      />
    ),
    [tenantURL, setTenantURL, l]
  );

  const documentField = useMemo(() => {
    const initialDocumentOptions = [
      { label: l('Passport'), value: 'ppt_object' },
      { label: l('Driver’s License'), value: 'dl_object' },
      { label: l('Any Document'), value: 'idcard_object' },
    ];

    return (
      <Box>
        <Typography kind='label' htmlFor='Environment'>
          {l('Which document you would like to verify?')}
        </Typography>
        <MenuInput
          options={initialDocumentOptions.map((option) => ({
            label: option.label,
            value: option.value,
            isChecked: option.value === documentType,
          }))}
          onChange={(selectedOption: Option) =>
            setDocumentType(selectedOption.value)
          }
          placeholder={l('Select Document')}
          className='Ext-IDDataWebSettings-content-menuInput'
          isSearchDisabled
        />
      </Box>
    );
  }, [l, documentType]);

  const isSaveDisabled =
    (isSaviyntSupportAdmin &&
      (isEmpty(license) ||
        isEmpty(tenantDNS) ||
        isEmpty(tenantURL) ||
        isEmpty(community))) ||
    isEmpty(dvcID) ||
    isEmpty(documentType) ||
    isUpdateInProgress;

  if (!isOpen) {
    return null;
  }

  const resetBanners = () => {
    setShowCriticalBanner(false);
  };

  return (
    <div
      className='Ext-SaviyntIDVSettings'
      role='dialog'
      aria-labelledby='setUpPageTitle'
      aria-hidden={!isOpen}>
      <div className='Ext-SaviyntIDVSettings-contentWrapper'>
        {provider ? (
          <Header
            currentProvider={currentProvider}
            provider={provider}
            resetBanners={resetBanners}
            toggleConfig={{
              DOCUMENT_TYPE: currentProvider?.config?.DOCUMENT_TYPE,
            }}
          />
        ) : (
          ''
        )}
        <AlertBanner
          colorTheme='Critical'
          title={errorMessage}
          description=''
          isVisible={showCriticalBanner}
          onCancel={() => setShowCriticalBanner(false)}
          isFloating
          position={{
            type: 'absolute',
            top: '8rem',
            left: '7rem',
          }}
          shouldAutoDismiss
          isColonVisible={false}
        />
        <div className='Ext-SaviyntIDVSettings-content'>
          {isSaviyntSupportAdmin && communityField}
          {isSaviyntSupportAdmin && tenantDNSField}
          {isSaviyntSupportAdmin && tenantURLField}
          {dvcIDField}
          {isSaviyntSupportAdmin && licenseField}
          {documentField}
        </div>
      </div>
      <div
        className={classNames(
          'Ext-SaviyntIDVSettings-footer',
          isMenuPinned && 'Ext-SaviyntIDVSettings-footer--isMenuPinned'
        )}>
        <FooterActionBar>
          <Button kind='outlined' onClick={onClose}>
            {l('Back')}
          </Button>
          <Button
            kind='filled'
            onClick={handleSave}
            isLoading={isUpdateInProgress}
            loaderText={l('Saving')}
            isDisabled={isSaveDisabled}>
            {l('Save')}
          </Button>
        </FooterActionBar>
      </div>
    </div>
  );
}

export default SaviyntIDVSettings;
